import { 
  sendSignInLinkToEmail, 
  signInWithEmailLink, 
  signOut,
  onAuthStateChanged,
  isSignInWithEmailLink
} from 'firebase/auth';
import { auth } from './config';
import { sendPasswordlessSignInLinkViaBackend } from '../utils/backendAuthApi';

// Configuration for the passwordless authentication
const actionCodeSettings = {
  // URL you want to redirect back to. The domain (www.example.com) for this
  // URL must be in the authorized domains list in the Firebase Console.
  // Uses environment variable if set, otherwise falls back to current origin
  // Development: http://localhost:3000/auth/action (set in .env)
  // Production: https://talyora.com/auth/action (set in .env.production)
  url: process.env.REACT_APP_AUTH_ACTION_URL || `${window.location.origin}/auth/action`,
  // This must be true for email link sign-in
  handleCodeInApp: true,
};

/**
 * Sends a sign-in link to the provided email using backend API with Resend custom template
 * @param {string} email - The email address to send the sign-in link to
 * @param {boolean} useBackend - Whether to use backend API (Resend) or Firebase directly
 * @returns {Promise<void>}
 */
export const sendPasswordlessSignInLink = async (email, useBackend = true) => {
  try {
    if (useBackend) {
      // Use backend API with custom Resend email template
      await sendPasswordlessSignInLinkViaBackend(email, actionCodeSettings.url);
      // Store the email locally for later verification
      window.localStorage.setItem('emailForSignIn', email);
      console.log('Sign-in link sent via backend with custom template to:', email);
    } else {
      // Fallback to Firebase's default email template
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Store the email locally for later verification
      window.localStorage.setItem('emailForSignIn', email);
      console.log('Sign-in link sent via Firebase to:', email);
    }
  } catch (error) {
    console.error('Error sending sign-in link:', error);
    throw error;
  }
};

/**
 * Completes the sign-in process using the email link
 * @param {string} emailLink - The sign-in link from the email
 * @param {string} email - The email address (optional, will try to get from localStorage)
 * @returns {Promise<Object>} User object
 */
export const completePasswordlessSignIn = async (emailLink, email = null) => {
  try {
    // Check if the link is valid
    if (!isSignInWithEmailLink(auth, emailLink)) {
      throw new Error('Invalid sign-in link');
    }

    // Get email from parameter or localStorage
    let userEmail = email;
    if (!userEmail) {
      userEmail = window.localStorage.getItem('emailForSignIn');
    }

    if (!userEmail) {
      throw new Error('Email is required to complete sign-in. Please use the sign-in link from your email.');
    }

    // Complete the sign-in
    const result = await signInWithEmailLink(auth, userEmail, emailLink);
    
    // Clear the stored email
    window.localStorage.removeItem('emailForSignIn');
    
    return result.user;
  } catch (error) {
    console.error('Error completing sign-in:', error);
    throw error;
  }
};

/**
 * Signs out the current user
 * @returns {Promise<void>}
 */
export const signOutUser = async () => {
  try {
    await signOut(auth);
    console.log('User signed out successfully');
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Sets up an authentication state listener
 * @param {Function} callback - Callback function to handle auth state changes
 * @returns {Function} Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Checks if the current URL contains a sign-in link
 * @returns {boolean}
 */
export const isSignInLink = () => {
  return isSignInWithEmailLink(auth, window.location.href);
};

/**
 * Gets the current user
 * @returns {Object|null} Current user object or null
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};